/**
 * Confirmation Validation Service
 * 
 * Provides utilities for checking fields that require user confirmation before form submission.
 * This allows showing a confirmation dialog when specific field values match configured criteria.
 * 
 * Configuration is passed from JSON config at the section/form level.
 */

import { getIn } from 'formik';

/**
 * Supported validation rule types for confirmation dialogs
 */
export const CONFIRMATION_RULE_TYPES = {
  RADIO: 'radio',           // Check if radio value is in a list of trigger values
  CHECKBOX: 'checkbox',     // Check if checkbox value matches trigger values
  YEAR_PICKER: 'yearPicker', // Check year against min/max/range
  TEXT: 'text',             // Check if text value matches pattern or values
  NUMBER: 'number',         // Check if number is in a specific range
  DATE: 'date',             // Check if date is in a specific range
  SELECT: 'select',         // Check if selected value is in trigger values
  MULTISELECT: 'multiselect', // Check if any selected value is in trigger values
  // Backend-compatible validation types
  EQUALS: 'equals',         // Check if value equals any of the params (like backend)
  YEAR_RANGE: 'yearRange',  // Check year range (like backend)
  DATE_RANGE: 'dateRange',  // Check date range (like backend)
  MIN: 'min',               // Check minimum value (like backend)
  MAX: 'max',               // Check maximum value (like backend)
};

/**
 * Evaluate a single confirmation validation rule
 * 
 * @param {Object} rule - The validation rule configuration
 * @param {*} fieldValue - The current field value
 * @param {Object} formValues - All form values (for cross-field validations)
 * @returns {Object} - { triggered: boolean, message: string }
 */
export const evaluateConfirmationRule = (rule, fieldValue, formValues) => {
  const { type, triggerValues, condition, params, message } = rule;

  // Skip if field has no value
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return { triggered: false, message: '' };
  }

  let triggered = false;
  let customMessage = message || '';

  switch (type) {
    case CONFIRMATION_RULE_TYPES.RADIO:
    case CONFIRMATION_RULE_TYPES.SELECT:
      // Check if selected value is in the list of trigger values
      if (Array.isArray(triggerValues) && triggerValues.includes(fieldValue)) {
        triggered = true;
        customMessage = customMessage.replace('{value}', fieldValue);
      }
      break;

    // Backend-compatible: equals type - check if value equals any of the params
    case CONFIRMATION_RULE_TYPES.EQUALS:
      if (Array.isArray(params) && params.includes(fieldValue)) {
        triggered = true;
        customMessage = customMessage.replace('{value}', fieldValue);
      }
      break;

    // Backend-compatible: yearRange type - check if year is outside the range
    case CONFIRMATION_RULE_TYPES.YEAR_RANGE:
      triggered = evaluateYearPickerRule(fieldValue, 'yearRange', params);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{min}', params?.[0] || '')
          .replace('{max}', params?.[1] || '');
      }
      break;

    // Backend-compatible: dateRange type - check if date is outside the range
    case CONFIRMATION_RULE_TYPES.DATE_RANGE:
      // params: [format, minDate, maxDate] or [minDate, maxDate]
      const dateParams = params?.length === 3 ? [params[1], params[2]] : params;
      triggered = evaluateDateRule(fieldValue, 'range', dateParams);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{min}', dateParams?.[0] || '')
          .replace('{max}', dateParams?.[1] || '');
      }
      break;

    // Backend-compatible: min type - check if value is less than minimum
    case CONFIRMATION_RULE_TYPES.MIN:
      triggered = evaluateNumberRule(fieldValue, 'min', params);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{min}', params?.[0] || '');
      }
      break;

    // Backend-compatible: max type - check if value is greater than maximum
    case CONFIRMATION_RULE_TYPES.MAX:
      triggered = evaluateNumberRule(fieldValue, 'max', params);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{max}', params?.[0] || '');
      }
      break;

    case CONFIRMATION_RULE_TYPES.CHECKBOX:
      // For checkbox, check if the boolean value matches the trigger
      if (typeof fieldValue === 'boolean') {
        const triggerBool = triggerValues?.[0] === true || triggerValues?.[0] === 'true';
        if (fieldValue === triggerBool) {
          triggered = true;
        }
      }
      break;

    case CONFIRMATION_RULE_TYPES.MULTISELECT:
      // Check if any selected value is in the trigger values
      if (Array.isArray(fieldValue) && Array.isArray(triggerValues)) {
        const matchingValues = fieldValue.filter(v => triggerValues.includes(v));
        if (matchingValues.length > 0) {
          triggered = true;
          customMessage = customMessage.replace('{values}', matchingValues.join(', '));
        }
      }
      break;

    case CONFIRMATION_RULE_TYPES.YEAR_PICKER:
      triggered = evaluateYearPickerRule(fieldValue, condition, params);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{min}', params?.[0] || '')
          .replace('{max}', params?.[1] || '');
      }
      break;

    case CONFIRMATION_RULE_TYPES.NUMBER:
      triggered = evaluateNumberRule(fieldValue, condition, params);
      if (triggered) {
        customMessage = customMessage
          .replace('{value}', fieldValue)
          .replace('{min}', params?.[0] || '')
          .replace('{max}', params?.[1] || '');
      }
      break;

    case CONFIRMATION_RULE_TYPES.TEXT:
      triggered = evaluateTextRule(fieldValue, condition, params, triggerValues);
      if (triggered) {
        customMessage = customMessage.replace('{value}', fieldValue);
      }
      break;

    case CONFIRMATION_RULE_TYPES.DATE:
      triggered = evaluateDateRule(fieldValue, condition, params);
      if (triggered) {
        customMessage = customMessage.replace('{value}', fieldValue);
      }
      break;

    default:
      // For unknown types, check triggerValues if provided
      if (Array.isArray(triggerValues) && triggerValues.includes(fieldValue)) {
        triggered = true;
        customMessage = customMessage.replace('{value}', fieldValue);
      }
      break;
  }

  return { triggered, message: customMessage };
};

/**
 * Evaluate year picker rules
 */
const evaluateYearPickerRule = (value, condition, params) => {
  const year = parseInt(value, 10);
  if (isNaN(year)) return false;

  const [param1, param2] = params || [];

  switch (condition) {
    case 'min':
      // Trigger if year is LESS than minimum
      return year < parseInt(param1, 10);

    case 'max':
      // Trigger if year is GREATER than maximum
      return year > parseInt(param1, 10);

    case 'yearRange':
    case 'range':
      // Trigger if year is OUTSIDE the range
      const minYear = parseInt(param1, 10);
      const maxYear = parseInt(param2, 10);
      return year < minYear || year > maxYear;

    case 'equals':
      // Trigger if year EQUALS the specified value
      return year === parseInt(param1, 10);

    case 'notEquals':
      // Trigger if year does NOT equal the specified value
      return year !== parseInt(param1, 10);

    default:
      return false;
  }
};

/**
 * Evaluate number rules
 */
const evaluateNumberRule = (value, condition, params) => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;

  const [param1, param2] = params || [];

  switch (condition) {
    case 'min':
      return num < parseFloat(param1);

    case 'max':
      return num > parseFloat(param1);

    case 'range':
      return num < parseFloat(param1) || num > parseFloat(param2);

    case 'equals':
      return num === parseFloat(param1);

    case 'notEquals':
      return num !== parseFloat(param1);

    case 'greaterThan':
      return num > parseFloat(param1);

    case 'lessThan':
      return num < parseFloat(param1);

    default:
      return false;
  }
};

/**
 * Evaluate text rules
 */
const evaluateTextRule = (value, condition, params, triggerValues) => {
  const strValue = String(value).trim();

  switch (condition) {
    case 'contains':
      return strValue.toLowerCase().includes(String(params?.[0] || '').toLowerCase());

    case 'startsWith':
      return strValue.toLowerCase().startsWith(String(params?.[0] || '').toLowerCase());

    case 'endsWith':
      return strValue.toLowerCase().endsWith(String(params?.[0] || '').toLowerCase());

    case 'pattern':
    case 'regex':
      try {
        const regex = new RegExp(params?.[0] || '');
        return regex.test(strValue);
      } catch {
        return false;
      }

    case 'length':
      // Trigger if length is outside specified bounds
      const [minLen, maxLen] = params || [];
      if (minLen !== undefined && strValue.length < parseInt(minLen, 10)) return true;
      if (maxLen !== undefined && strValue.length > parseInt(maxLen, 10)) return true;
      return false;

    case 'includes':
    default:
      // Check if value is in triggerValues array
      if (Array.isArray(triggerValues)) {
        return triggerValues.some(tv => 
          String(tv).toLowerCase() === strValue.toLowerCase()
        );
      }
      return false;
  }
};

/**
 * Evaluate date rules (expects DD/MM/YYYY format)
 */
const evaluateDateRule = (value, condition, params) => {
  // Parse date value (DD/MM/YYYY)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(p => parseInt(p, 10));
    return new Date(year, month - 1, day);
  };

  const dateValue = parseDate(value);
  if (!dateValue || isNaN(dateValue.getTime())) return false;

  const [param1, param2] = params || [];
  const minDate = param1 ? parseDate(param1) : null;
  const maxDate = param2 ? parseDate(param2) : null;

  switch (condition) {
    case 'before':
      return minDate && dateValue < minDate;

    case 'after':
      return minDate && dateValue > minDate;

    case 'range':
      // Trigger if date is OUTSIDE the range
      if (minDate && dateValue < minDate) return true;
      if (maxDate && dateValue > maxDate) return true;
      return false;

    default:
      return false;
  }
};

/**
 * Get field value from form values using dot notation path
 * 
 * @param {Object} formValues - All form values
 * @param {string} fieldKey - Field key (can be dot notation like "categoryKey.fieldKey")
 * @returns {*} - The field value
 */
export const getFieldValue = (formValues, fieldKey) => {
  if (!formValues || !fieldKey) return undefined;

  // Use Formik's getIn utility for nested paths
  return getIn(formValues, fieldKey);
};

/**
 * Check all confirmation validation rules and return triggered rules
 * Supports both frontend rule format and backend validation format
 * 
 * Frontend format:
 * {
 *   rules: [{ fieldKey, type, triggerValues/params, message }]
 * }
 * 
 * Backend format:
 * {
 *   fields: [{ key, validation: [{ type, params }] }]
 * }
 * 
 * @param {Object} confirmationConfig - The confirmation validation configuration
 * @param {Object} formValues - All form values
 * @returns {Object} - { shouldConfirm: boolean, triggeredRules: Array, message: string }
 */
export const checkConfirmationValidations = (confirmationConfig, formValues) => {
  if (!confirmationConfig?.enabled) {
    return { shouldConfirm: false, triggeredRules: [], message: '' };
  }

  const triggeredRules = [];

  // Support backend format with 'fields' array containing 'validation' arrays
  if (confirmationConfig.fields && Array.isArray(confirmationConfig.fields)) {
    for (const field of confirmationConfig.fields) {
      const { key: fieldKey, validation: validations, fieldLabel } = field;
      
      if (!fieldKey || !validations || !Array.isArray(validations)) continue;

      // Get full field path - check if it needs category prefix
      const fullFieldKey = confirmationConfig.categoryKey 
        ? `${confirmationConfig.categoryKey}.${fieldKey}` 
        : fieldKey;
      
      const fieldValue = getFieldValue(formValues, fullFieldKey);

      // Check each validation rule for this field
      for (const validation of validations) {
        const rule = {
          fieldKey: fullFieldKey,
          fieldLabel: fieldLabel || field.label || fieldKey,
          type: validation.type,
          params: validation.params,
          message: validation.message || '',
        };

        const result = evaluateConfirmationRule(rule, fieldValue, formValues);

        if (result.triggered) {
          triggeredRules.push({
            ...rule,
            fieldValue,
            message: result.message,
          });
        }
      }
    }
  }
  
  // Support frontend format with 'rules' array
  if (confirmationConfig.rules && Array.isArray(confirmationConfig.rules)) {
    for (const rule of confirmationConfig.rules) {
      const { fieldKey, fieldLabel } = rule;
      
      if (!fieldKey) continue;

      const fieldValue = getFieldValue(formValues, fieldKey);
      const result = evaluateConfirmationRule(rule, fieldValue, formValues);

      if (result.triggered) {
        triggeredRules.push({
          ...rule,
          fieldKey,
          fieldLabel: fieldLabel || fieldKey,
          fieldValue,
          message: result.message,
        });
      }
    }
  }

  const shouldConfirm = triggeredRules.length > 0;
  
  // Build a combined message if needed
  let message = confirmationConfig.message || 'The following fields require your attention:';
  
  if (shouldConfirm && triggeredRules.length > 0) {
    // Add specific field messages
    const fieldMessages = triggeredRules
      .map(r => {
        const fieldName = r.fieldLabel || r.fieldKey;
        const value = r.fieldValue;
        const ruleMessage = r.message || `Value "${value}" may need review`;
        return `• ${fieldName}: ${ruleMessage}`;
      })
      .join('\n');
    
    if (fieldMessages) {
      message = `${message}\n\n${fieldMessages}`;
    }
  }

  return {
    shouldConfirm,
    triggeredRules,
    message,
    buttonLabel: confirmationConfig.buttonLabel || 'Save and Continue',
    cancelButtonLabel: confirmationConfig.cancelButtonLabel || 'Review',
    title: confirmationConfig.title || 'Confirmation Required',
  };
};

/**
 * Extract confirmation validation config from section/form config
 * 
 * @param {Object} config - The form/section configuration
 * @returns {Object|null} - The confirmation validation config or null
 */
export const getConfirmationValidationConfig = (config) => {
  if (!config) return null;

  // Check at section level
  if (config.confirmationValidation) {
    return config.confirmationValidation;
  }

  // Check at form level (inside categories or fields)
  if (config.form?.confirmationValidation) {
    return config.form.confirmationValidation;
  }

  return null;
};

/**
 * Build confirmation validation config from field-level configurations
 * This collects all fields with confirmationValidation property
 * 
 * @param {Object} config - The form configuration
 * @returns {Object} - Aggregated confirmation validation config
 */
export const buildConfirmationConfigFromFields = (config) => {
  const rules = [];

  const processFields = (fields, categoryKey = '') => {
    if (!fields || !Array.isArray(fields)) return;

    for (const field of fields) {
      // Skip if field is hidden or has no key
      if (!field.key || field.key === 'divider' || field.key === 'testSpace') continue;

      const fullFieldKey = categoryKey ? `${categoryKey}.${field.key}` : field.key;

      // Check if field has confirmation validation config
      if (field.confirmationValidation) {
        const fieldRule = {
          fieldKey: fullFieldKey,
          fieldLabel: field.label || field.key,
          ...field.confirmationValidation,
        };
        rules.push(fieldRule);
      }

      // Process nested fields if any
      if (field.fields && Array.isArray(field.fields)) {
        processFields(field.fields, fullFieldKey);
      }
    }
  };

  const processCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return;

    for (const category of categories) {
      if (category.fields) {
        processFields(category.fields, category.key);
      }
    }
  };

  // Process categories if present
  if (config.categories) {
    processCategories(config.categories);
  }

  // Process root-level fields if present
  if (config.fields) {
    processFields(config.fields);
  }

  // Return aggregated config
  if (rules.length > 0) {
    return {
      enabled: true,
      rules,
      message: config.confirmationValidationMessage || 'The following fields require your confirmation before saving:',
      buttonLabel: config.confirmationValidationButtonLabel || 'Confirm & Save',
    };
  }

  return null;
};

/**
 * Get the effective confirmation validation config
 * First checks section-level config, then builds from field-level configs
 * 
 * @param {Object} config - The form/section configuration
 * @returns {Object|null} - The effective confirmation validation config
 */
export const getEffectiveConfirmationConfig = (config) => {
  // First, check for section-level config
  const sectionConfig = getConfirmationValidationConfig(config);
  if (sectionConfig?.enabled) {
    return sectionConfig;
  }

  // Then, build from field-level configs
  const fieldConfig = buildConfirmationConfigFromFields(config);
  if (fieldConfig?.enabled) {
    return fieldConfig;
  }

  return null;
};
